<template>
  <div>
    <div class="border border-gray-200 rounded-md p-4 bg-white shadow-sm">
      <div class="">
        <!-- Header -->
        <div class="flex items-center justify-between">
          <!-- Report type header -->
          <ReportStatusBadge :report="report" />

          <div class="text-xs text-gray-500 flex items-center gap-1">
            <Calendar class="w-3.5 h-3.5" />
            {{ formatDateUtil(report.created_at) }}
          </div>
        </div>
      </div>
      <hr class="my-4" />

      <!-- Description -->
      <div v-if="report.description" class="text-sm text-gray-700 mt-2 bg-blue-50 rounded-md mb-4">
        <h3 class="text-sm text-gray-500 uppercase">Strike Description</h3>
        <p class="text-sm text-gray-700 bg-gray-100 rounded-md p-4 mt-2">
          {{ report.description }}
        </p>
      </div>

       <!-- Evidence Notes -->
       <div v-if="report.evidence_notes" class="text-sm text-gray-700 mt-2 bg-blue-50 rounded-md mb-4">
        <h3 class="text-sm text-gray-500 uppercase">Notes</h3>
        <p class="text-sm text-gray-700 bg-gray-100 rounded-md p-4 mt-2 whitespace-pre-wrap">
          {{ report.evidence_notes }}
        </p>
      </div>

      <!-- Revocation Details -->
      <div v-if="report.is_revoked_strike">
        <hr class="my-4" />
        <h3 class="text-sm text-gray-500 uppercase">Revocation Details</h3>
        <div class="text-sm mt-2 text-green-800 p-4 bg-green-50 rounded-md mb-4">
          <div class="space-y-1">
            <div v-if="report.revoked_by_display">
              <span class="font-medium">Revoked by:</span> {{ report.revoked_by_display }} on
              {{ formatDateUtil(report.revoked_at || '') }}
            </div>
            <div v-if="report.revocation_reason">
              <span class="font-medium block mb-1">Reason:</span> {{ report.revocation_reason }}
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div
        v-if="showActions && ['PENDING', 'UNDER_REVIEW'].includes(report.status)"
        class="mt-4 flex justify-end gap-2"
      >
        <button
          @click="handleRejectReport"
          :disabled="isSubmitting"
          class="text-xs py-2 px-4 bg-black text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Loader2 v-if="isSubmitting" class="mr-2 h-4 w-4 animate-spin inline" />
          Reject
        </button>
        <button
          @click="showStrikeForm = true"
          :disabled="isSubmitting"
          class="text-xs py-2 px-4 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Issue Warning
        </button>
      </div>

      <div v-if="showActions && report.status === 'APPROVED'" class="mt-4 flex justify-end">
        <button
          @click="showRevokeForm = true"
          class="text-xs border-[1px] border-red-400 rounded-md px-3 py-2 flex items-center gap-2 bg-none text-red-500 hover:text-red-600 hover:bg-red-50 transition-colors"
        >
          <X class="w-4 h-4" />
          Revoke Warning
        </button>
      </div>

      <div
        v-if="report.status === 'REVOKED'"
        class="mt-4 flex justify-end w-fit text-xs border-[1px] border-gray-200 rounded-md px-4 py-2 ml-auto text-gray-500"
      >
        Revoked
      </div>

      <div
        v-if="report.status === 'REJECTED'"
        class="mt-4 flex justify-end w-fit text-xs border-[1px] border-gray-200 rounded-md px-4 py-2 ml-auto text-gray-500"
      >
        Rejected
      </div>
    </div>

    <!-- Forms -->
    <StrikeForm
      v-model="showStrikeForm"
      :is-submitting="isSubmitting"
      @submit="handleStrikeSubmit"
    />

    <RevokeForm
      v-model="showRevokeForm"
      :is-submitting="isSubmitting"
      @submit="handleRevokeSubmit"
    />
  </div>
</template>

<script setup lang="ts">
import { formatDate as formatDateUtil } from '@/utils/formatDate'
import type { Report, FellowReport } from '@/types/reports'
import { ref } from 'vue'
import { Calendar, X, Loader2 } from 'lucide-vue-next'
import ReportStatusBadge from './ReportStatusBadge.vue'
import StrikeForm from './StrikeForm.vue'
import RevokeForm from './RevokeForm.vue'
import { useReportActions } from '@/composables/useReportActions'

const props = defineProps<{
  report: Report | FellowReport
  showActions?: boolean
}>()

const emit = defineEmits<{
  (e: 'report-updated', report: Report | FellowReport): void
}>()

const showStrikeForm = ref(false)
const showRevokeForm = ref(false)

const { isSubmitting, rejectReport, issueStrike, revokeStrike } = useReportActions(props.report)

const handleRejectReport = async () => {
  try {
    const updatedReport = await rejectReport()
    if (updatedReport) {
      emit('report-updated', updatedReport)
    }
  } catch (error) {
    // Error is already logged in the composable
  }
}

const handleStrikeSubmit = async (data: { severity: string; review_notes?: string }) => {
  try {
    const updatedReport = await issueStrike(data)
    if (updatedReport) {
      emit('report-updated', updatedReport)
      showStrikeForm.value = false
    }
  } catch (error) {
    // Error is already logged in the composable
  }
}

const handleRevokeSubmit = async (data: { reason: string }) => {
  try {
    const updatedReport = await revokeStrike(data)
    if (updatedReport) {
      emit('report-updated', updatedReport)
      showRevokeForm.value = false
    }
  } catch (error) {
    // Error is already logged in the composable
  }
}
</script>
